import { concat } from '@ember/helper';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { camelize } from 'frontend-utils/utils/convert-case';
import HotelAndMarketRevenueInsightFixedKpiHeaderItem from 'lumen/components/hotel-and-market/revenue-insight/fixed-kpi-header-item';
import type { RevenueInsightSegmentationValue } from 'lumen/models';
import {
  type Kpi,
  type KpiCategory,
  KPIS,
} from 'lumen/utils/constants/ri-constant-values';
import {
  type FixedScrollHeader,
  fixedScrollHeader,
} from 'lumen/utils/decorators/fixed-scroll-header';
import { valueFormat } from 'lumen/utils/revenue-insight/value-formatting';

interface KpiBlock {
  changeTab: (kpi: Kpi) => void;
  kpis: Kpi[];
}

interface HotelAndMarketRevenueInsightFixedKpiHeaderSignature {
  Args: {
    allSegmentationData?: RevenueInsightSegmentationValue[];
    kpi: Kpi;
    kpiBlocks: KpiBlock[];
    kpiCategory: KpiCategory;
  };
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
interface HotelAndMarketRevenueInsightFixedKpiHeader
  extends
    FixedScrollHeader,
    Component<HotelAndMarketRevenueInsightFixedKpiHeaderSignature> {}

// 242px is actual scrollTop at which the metric blocks will fully disappear from the screen. We subtract 40px to make the fixed header appear a bit earlier
@fixedScrollHeader({ scrollTopTrigger: 202 })
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class HotelAndMarketRevenueInsightFixedKpiHeader extends Component<HotelAndMarketRevenueInsightFixedKpiHeaderSignature> {
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.allSegmentationData.length',
    'args.{kpi,kpiBlocks,kpiCategory}',
    'riSelections.{bobKey,isCancellationsPercentageView}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get headerItems() {
    const { bobKey, isCancellationsPercentageView } = this.riSelections;

    return this.args.kpiBlocks
      .map((kpiBlock) =>
        kpiBlock.kpis.map((kpi) => {
          let formattedValue = '-';

          if (
            this.args.allSegmentationData?.length &&
            this.args.allSegmentationData[0]
          ) {
            const showPercentage =
              isCancellationsPercentageView || kpi === KPIS.OCCUPANCY;

            const value =
              this.args.allSegmentationData[0].calculatedValues[bobKey][
                camelize(`${kpi}_${this.args.kpiCategory}`)
              ];
            formattedValue = valueFormat(value, showPercentage, this.intl);
          }

          return {
            kpi,
            formattedValue,
            changeTab: kpiBlock.changeTab,
          };
        }),
      )
      .flat();
  }

  <template>
    <div
      class="ri-perf-metric-sticky-header-wrapper
        {{if this.showHeader 'visible'}}"
    >
      <div class="ri-perf-metric-sticky-header-content">
        {{#each this.headerItems as |headerItem|}}
          <HotelAndMarketRevenueInsightFixedKpiHeaderItem
            @changeTab={{headerItem.changeTab}}
            @kpi={{@kpi}}
            @kpiCategory={{@kpiCategory}}
            @kpiName={{headerItem.kpi}}
            @title={{t (concat "revenue_insight." headerItem.kpi)}}
            @value={{headerItem.formattedValue}}
          />
        {{/each}}
      </div>
    </div>
  </template>
}

export default HotelAndMarketRevenueInsightFixedKpiHeader;
